import { useMemo, useState } from 'react';
import { useI18n } from '../hooks/useI18n';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';

// ── Moon phase calculation (local, no API) ──────────────────────────────────
// Based on Julian Day Number synodic month algorithm
function getMoonPhase(date: Date): { phase: number; name: string; emoji: string; illumination: number; description: string } {
  const year  = date.getFullYear();
  const month = date.getMonth() + 1;
  const day   = date.getDate();

  // Julian Day Number
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  const jdn = day + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;

  const synodicMonth = 29.53058867;
  const knownNew = 2451550.1; // Jan 6, 2000 new moon
  const daysSince = jdn - knownNew;
  const phase = ((daysSince % synodicMonth) + synodicMonth) % synodicMonth;
  const illumination = Math.round((1 - Math.cos((2 * Math.PI * phase) / synodicMonth)) / 2 * 100);

  let name: string, emoji: string, description: string;
  if (phase < 1.85) {
    name = 'Lua Nova'; emoji = '🌑';
    description = 'Tempo de novos começos, intenções e plantio de sementes. Momento ideal para definir metas e iniciar projetos.';
  } else if (phase < 7.38) {
    name = 'Lua Crescente'; emoji = '🌒';
    description = 'Energia expansiva e de crescimento. Ideal para dar os primeiros passos nos projetos iniciados na Lua Nova.';
  } else if (phase < 9.22) {
    name = 'Quarto Crescente'; emoji = '🌓';
    description = 'Momento de ação e decisão. A energia está em alta, tome atitudes concretas em direção às suas metas.';
  } else if (phase < 14.77) {
    name = 'Lua Gibosa Crescente'; emoji = '🌔';
    description = 'Período de refinamento e ajuste. Revise seus planos e fortaleça o que já está funcionando.';
  } else if (phase < 16.61) {
    name = 'Lua Cheia'; emoji = '🌕';
    description = 'Pico de energia e revelação. Emoções e intuições estão amplificadas. Momento de colher frutos e celebrar conquistas.';
  } else if (phase < 22.15) {
    name = 'Lua Gibosa Minguante'; emoji = '🌖';
    description = 'Tempo de gratidão e compartilhamento. Distribua suas conquistas e reconheça o que foi alcançado.';
  } else if (phase < 23.99) {
    name = 'Quarto Minguante'; emoji = '🌗';
    description = 'Período de limpeza e liberação. Ideal para encerrar ciclos, perdoar e soltar o que não serve mais.';
  } else if (phase < 29.53) {
    name = 'Lua Crescente Minguante'; emoji = '🌘';
    description = 'Momento de descanso e introspecção. Prepare-se para o próximo ciclo com silêncio e reflexão.';
  } else {
    name = 'Lua Nova'; emoji = '🌑';
    description = 'Tempo de novos começos, intenções e plantio de sementes.';
  }

  return { phase, name, emoji, illumination, description };
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getMoonEmoji(phase: number): string {
  if (phase < 1.85) return '🌑';
  if (phase < 7.38) return '🌒';
  if (phase < 9.22) return '🌓';
  if (phase < 14.77) return '🌔';
  if (phase < 16.61) return '🌕';
  if (phase < 22.15) return '🌖';
  if (phase < 23.99) return '🌗';
  return '🌘';
}

function getNextPhases(from: Date): { name: string; emoji: string; date: Date }[] {
  const targets = [1.85, 9.22, 16.61, 23.99]; // new, first quarter, full, last quarter
  const targetNames = ['Lua Nova', 'Quarto Crescente', 'Lua Cheia', 'Quarto Minguante'];
  const targetEmojis = ['🌑', '🌓', '🌕', '🌗'];
  const synodicMonth = 29.53058867;

  const results: { name: string; emoji: string; date: Date }[] = [];
  let current = from;

  for (let i = 0; i < 60; i++) {
    const next = new Date(current.getTime() + 24 * 60 * 60 * 1000);
    const p = getMoonPhase(next);
    for (let j = 0; j < targets.length; j++) {
      const prev = getMoonPhase(current);
      if (prev.phase < targets[j] && p.phase >= targets[j] && p.phase < targets[j] + 2) {
        results.push({ name: targetNames[j], emoji: targetEmojis[j], date: next });
      }
    }
    // Handle wrap-around for new moon
    if (prev_wrap(current, synodicMonth)) {
      results.push({ name: 'Lua Nova', emoji: '🌑', date: next });
    }
    current = next;
    if (results.length >= 4) break;
  }
  return results.slice(0, 4);
}

function prev_wrap(date: Date, _synodicMonth: number): boolean {
  const p = getMoonPhase(date);
  const next = new Date(date.getTime() + 24 * 60 * 60 * 1000);
  const pn = getMoonPhase(next);
  return p.phase > 28 && pn.phase < 2;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export function MoonPhasesPage() {
  useSystemUsageTracker('fases_lua');
  const { t }  = useI18n();
  const today = useMemo(() => new Date(), []);
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selected,  setSelected]  = useState<Date>(today);

  const currentMoon = useMemo(() => getMoonPhase(selected), [selected]);
  const nextPhases  = useMemo(() => getNextPhases(today), [today]);

  const daysInMonth    = getDaysInMonth(viewYear, viewMonth);
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const calDays: (Date | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewYear, viewMonth, i + 1)),
  ];

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.moonPhases.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.moonPhases.subtitle')}
        </p>
      </div>

      {/* Today's moon */}
      <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center', backgroundColor: 'rgba(31,78,95,0.04)', border: '1px solid var(--border-gold)' }}>
        <span style={{ fontSize: '4rem', display: 'block', marginBottom: '0.5rem' }}>{currentMoon.emoji}</span>
        <p className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{currentMoon.name}</p>
        <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>
          {t('pages.moonPhases.illuminated', { n: String(currentMoon.illumination) })} · {selected.toLocaleDateString('pt-BR')}
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.6 }}>{currentMoon.description}</p>
      </div>

      {/* Upcoming phases */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
          {t('pages.moonPhases.upcomingPhases')}
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {nextPhases.map((p, i) => (
            <div key={i} style={{ flex: '1 1 120px', textAlign: 'center', padding: '0.75rem', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
              <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.4rem' }}>{p.emoji}</span>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: '0 0 0.2rem', fontWeight: 600 }}>{p.name}</p>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{p.date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="card">
        {/* Month navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <button className="btn btn-ghost btn-sm" onClick={prevMonth}>‹</button>
          <p className="font-display" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            {MONTHS_PT[viewMonth]} {viewYear}
          </p>
          <button className="btn btn-ghost btn-sm" onClick={nextMonth}>›</button>
        </div>

        {/* Click hint */}
        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center', marginBottom: '0.5rem' }}>
          👆 Toque em uma data para ver a fase da lua naquele dia
        </p>

        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
          {WEEKDAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        {/* Days */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {calDays.map((d, i) => {
            if (!d) return <div key={i} />;
            const moon = getMoonPhase(d);
            const isToday = d.toDateString() === today.toDateString();
            const isSelected = d.toDateString() === selected.toDateString();
            return (
              <button key={i} onClick={() => setSelected(d)}
                style={{
                  background: isSelected ? 'var(--petrol)' : isToday ? 'var(--bg-elevated)' : 'transparent',
                  border: isToday && !isSelected ? '1px solid var(--border-gold)' : '1px solid transparent',
                  borderRadius: 'var(--radius-sm)', padding: '4px 2px', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px',
                }}>
                <span style={{ fontSize: '11px', color: isSelected ? '#0F1123' : 'var(--text-secondary)', fontWeight: isToday ? 700 : 400 }}>
                  {d.getDate()}
                </span>
                <span style={{ fontSize: '13px' }}>{getMoonEmoji(moon.phase)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Ritual suggestions */}
      <div className="card" style={{ marginTop: '1.5rem', backgroundColor: 'rgba(31,78,95,0.04)', border: '1px solid var(--border-gold)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
          {t('pages.moonPhases.ritualsFor')} {currentMoon.name}
        </p>
        <RitualSuggestion phase={currentMoon.name} />
      </div>
    </div>
  );
}

interface PhaseAdvice {
  rituals: string[];
  practical: string[];
  avoid: string[];
}

function RitualSuggestion({ phase }: { phase: string }) {
  const advice: Record<string, PhaseAdvice> = {
    'Lua Nova': {
      rituals: ['Escreva 3 intenções para o novo ciclo', 'Medite em silêncio por 10 minutos', 'Plante uma semente (literal ou simbólica)'],
      practical: ['Bom para começar dietas e novos hábitos', 'Ideal para agendar reuniões importantes', 'Cortar o cabelo agora estimula crescimento mais lento'],
      avoid: ['Evite confrontos e discussões, energia ainda está se formando', 'Não tome decisões irreversíveis'],
    },
    'Lua Crescente': {
      rituals: ['Tome uma ação concreta em direção ao seu objetivo', 'Crie um vision board', 'Pratique afirmações positivas'],
      practical: ['Boa fase para cortar o cabelo, crescerá mais rápido e forte', 'Ideal para iniciar projetos e negócios', 'Energie os cristais ao sol'],
      avoid: ['Evite estagnação, o momento pede movimento', 'Não adie o que pode ser feito agora'],
    },
    'Quarto Crescente': {
      rituals: ['Enfrente um desafio que vem adiando', 'Faça uma ligação importante', 'Tome uma decisão pendente'],
      practical: ['Ótimo para esportes e atividade física intensa', 'Cortar o cabelo favorece volume e força', 'Faça tratos e assine contratos neste período'],
      avoid: ['Cuidado com impulsividade nas decisões', 'Evite sobrecarregar a agenda'],
    },
    'Lua Gibosa Crescente': {
      rituals: ['Revise e ajuste seus planos', 'Agradeça pelo progresso', 'Prepare-se para colher'],
      practical: ['Ideal para finalizar projetos', 'Cortar o cabelo traz força e brilho', 'Bom para tratamentos estéticos e cuidados com a pele'],
      avoid: ['Evite iniciar coisas novas, finalize o que está em andamento'],
    },
    'Lua Cheia': {
      rituals: ['Carregue seus cristais na luz da lua', 'Faça um ritual de gratidão', 'Libere o que não serve mais (escreva e queime)'],
      practical: ['⚠️ Cuidado com violência e tensão emocional, energia está no pico', 'Evite cortar o cabelo se quiser crescimento lento', 'Ótimo para colheitas, conservas e fermentados', 'Não realize cirurgias, sangramento pode ser maior'],
      avoid: ['Evite conflitos e discussões, emoções estão amplificadas', 'Cuidado com excessos e impulsividade'],
    },
    'Lua Gibosa Minguante': {
      rituals: ['Compartilhe seu conhecimento', 'Pratique a generosidade', 'Expresse gratidão a quem ama'],
      practical: ['Bom para depilação, o resultado dura mais', 'Ideal para desintoxicação e limpezas', 'Cortar o cabelo aqui reduz o crescimento'],
      avoid: ['Evite excessos alimentares', 'Não inicie novos projetos ainda'],
    },
    'Quarto Minguante': {
      rituals: ['Faça faxina (física e energética)', 'Perdoe a si mesmo e aos outros', 'Encerre compromissos pendentes'],
      practical: ['Ótimo para cirurgias eletivas, menos sangramento', 'Bom para tratamentos de eliminação (jejum, desintoxicação)', 'Cortar o cabelo agora retarda o crescimento, ótimo para quem não quer cortar com frequência'],
      avoid: ['Evite assinar contratos de longo prazo', '⚠️ Não faça implantes ou procedimentos de volume, baixa receptividade'],
    },
    'Lua Crescente Minguante': {
      rituals: ['Descanse e recarregue as energias', 'Meditação profunda', 'Prepare suas intenções para a próxima Lua Nova'],
      practical: ['Ideal para repouso e recuperação', 'Bom para tratamentos de desintoxicação', 'Evite depilação, pele mais sensível'],
      avoid: ['Não tome grandes decisões, aguarde a Lua Nova', 'Evite esforço físico intenso'],
    },
  };

  const data = advice[phase] ?? advice['Lua Nova'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, marginBottom: '0.4rem' }}>✨ Rituais e práticas</p>
        <ul style={{ margin: 0, padding: '0 0 0 1.25rem' }}>
          {data.rituals.map((r, i) => (
            <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.35rem', lineHeight: 1.5 }}>{r}</li>
          ))}
        </ul>
      </div>
      <div>
        <p style={{ fontSize: 'var(--text-caption)', color: '#4ade80', fontWeight: 600, marginBottom: '0.4rem' }}>🌿 Conselhos práticos</p>
        <ul style={{ margin: 0, padding: '0 0 0 1.25rem' }}>
          {data.practical.map((r, i) => (
            <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.35rem', lineHeight: 1.5 }}>{r}</li>
          ))}
        </ul>
      </div>
      <div>
        <p style={{ fontSize: 'var(--text-caption)', color: '#f87171', fontWeight: 600, marginBottom: '0.4rem' }}>⚠️ Evitar nesta fase</p>
        <ul style={{ margin: 0, padding: '0 0 0 1.25rem' }}>
          {data.avoid.map((r, i) => (
            <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '0.35rem', lineHeight: 1.5 }}>{r}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
